import BaseModel from './base_model'
import { POI_TYPE } from './poi_type'

export class Geometry {
  type: string;
  coordinates: Array<any>;

  constructor(data: any) {
    this.type = data.type
    this.coordinates = data.coordinates
  }
}

export class FeatureCollection {
  type: string = 'FeatureCollection'
  features: Array<Feature>
  properties: {[id: string]: any } | null;

  constructor(data: any) {
    this.features = (data.features || []).map((f: any) => new Feature(f))
    this.properties = data.properties || null;
  }

  get json() {
    return {
      type: 'FeatureCollection',
      features: this.features.map(feature => feature.json)
    }
  }
}

export default class Feature extends BaseModel {
  type: "Feature" = 'Feature'
  id: string
  geometry: Geometry
  properties: any

  constructor(data: any) {
    super(data)
    this.id = data.id
    this.geometry = new Geometry(data.geometry)
    this.properties = data.properties || {}

    if (typeof this.properties.title_i18n === 'string') {
      this.properties.title_i18n = JSON.parse(this.properties.title_i18n)
    }

    if (this.isPoint) {
      if (!this.properties.images) {
        this.properties.images = []
      }

      if (!this.properties.range) {
        this.properties.range = 3
      }
    }

    if (typeof this.properties.images === 'string') {
      this.properties.images = JSON.parse(this.properties.images)
    }

    if (this.isLevelChanger && Array.isArray(this.properties.levels)) {
      this.properties.levels.forEach((level: number) => this.properties[`__level_${level}`] = true)
    }

    if (this.properties.type === 'text' && Array.isArray(this.properties.textFont)) {
      this.properties.textFont = this.properties.textFont[0]
    }

    if (typeof this.properties.metadata === 'undefined') {
      this.properties.metadata = {}
    }

    this.update()
  }

  update() {
    if (Array.isArray(this.properties.workingHours)) {
      let hasEntry = false;
      this.properties.workingHours.forEach((entry: (string | null)[]) => {
        const open = entry[0];
        const close = entry[1];
        const hasOpen = open && open !== '00:00';
        const hasClose = close && close !== '00:00';
        if (hasOpen || hasClose) {
          hasEntry = true;
        }
      });
      this.properties._workingHoursEnabled = hasEntry;
    }
  }

  setTitle(title: string, lang = 'en') {
    if (typeof this.properties.title_18n === 'undefined') {
      this.properties.title_18n = {}
    }
    this.properties.title_18n[lang] = title
  }

  hasLevel(level: number) {
    if (this.isLevelChanger) {
      return this.properties.levels.includes(level)
    } else {
      return this.properties.level === level
    }
  }

  get isEditable() {
    return this.isPoint || this.isLineString || this.isPathLineString || this.isPolygon
  }

  get isPoint() {
    return this.geometry.type === 'Point'
  }

  get isPolygon() {
    return this.geometry.type === 'Polygon' || this.geometry.type === 'MultiPolygon'
  }

  get isLineString() {
    return (this.geometry.type === 'LineString' || this.geometry.type === 'MultiLineString') && this.properties.class !== 'path'
  }

  get isPathLineString() {
    return (this.geometry.type === 'LineString' || this.geometry.type === 'MultiLineString') && this.properties.class === 'path'
  }

  get isHazard() {
    return this.properties.type === POI_TYPE.HAZARD
  }

  get isLandmark() {
    return this.properties.type === POI_TYPE.LANDMARK
  }

  get isDoor() {
    return this.properties.type === POI_TYPE.DOOR
  }

  get isEntrance() {
    return this.properties.type === POI_TYPE.ENTRANCE
  }

  get isDecisionPoint() {
    return this.properties.type === POI_TYPE.DECISION
  }

  get isTicketGate() {
    return this.properties.type === POI_TYPE.TICKET_GATE
  }

  get isElevator() {
    return this.properties.type === POI_TYPE.ELEVATOR
  }

  get isEscalator() {
    return this.properties.type === POI_TYPE.ESCALATOR
  }

  get isStairCase() {
    return this.properties.type === POI_TYPE.STAIRCASE
  }

  get isRamp() {
    return this.properties.type === POI_TYPE.RAMP
  }

  get isLevelChanger() {
    return this.isElevator || this.isEscalator || this.isStairCase
  }

  get isConnector() {
    return this.properties.type === POI_TYPE.CONNECTOR
  }

  get isText() {
    return this.properties.type === 'text'
  }

  get isSynthetic() {
    let keys = ['id', 'place_id', 'floor_id', 'level']
    if (this.geometry.type === 'LineString' || this.geometry.type === 'MultiLineString') {
      keys.push('class')
    }
    return Object.keys(this.properties).map(key => !keys.includes(key)).filter(i => i).length === 0
  }

  get isRoom() {
    return this.properties.room
  }

  get isRouting() {
    return this.properties.usecase === 'routing'
  }

  get json() {
    const clone = JSON.parse(JSON.stringify(this))
    if (clone.properties.metadata && typeof clone.properties.metadata !== 'object') {
      try {
        clone.properties.metadata = JSON.parse(clone.properties.metadata)
      } catch (e) {
        console.error('feature parsing failed:', clone.properties.metadata)
      }
    }
    Object.keys(clone.properties).forEach(key => {
      if (key.match('__level')) {
        delete clone.properties.key
      }
    })
    return clone
  }

  static point(id: string, latitude: number, longitude: number, properties?: any) {
    return new Feature({
      id,
      geometry: {
        type: 'Point',
        coordinates: [longitude, latitude]
      },
      properties
    })
  }
}
